import { logoPrincipal } from '../assets';
import { useEffect, useRef, useState } from 'react';

const ACCORDION_ITEMS = [
  {
    number: '01',
    title: 'Arquitectura de Marca',
    description:
      'Definimos el posicionamiento, la narrativa y la identidad visual para que tu marca sea reconocible, coherente y memorable.',
    includes: ['Posicionamiento y propuesta de valor', 'Identidad visual y lineamientos', 'Mensaje y narrativa de marca'],
  },
  {
    number: '02',
    title: 'Arquitectura Digital',
    description:
      'Diseñamos y construimos el ecosistema digital que sostiene el crecimiento: sitio web, estructura de conversión y experiencia premium.',
    includes: ['Diseño UX/UI', 'Desarrollo web y landing pages', 'Optimización de conversión'],
  },
  {
    number: '03',
    title: 'Producción y Contenido',
    description:
      'Creamos los activos visuales y audiovisuales que representan tu marca con calidad profesional y consistencia.',
    includes: ['Producción audiovisual', 'Fotografía profesional (corporativa y producto)', 'Piezas de contenido para redes'],
  },
  {
    number: '04',
    title: 'Activación y Escalamiento',
    description:
      'Lanzamos, optimizamos y escalamos con estrategia basada en datos para que la marca crezca de forma sostenida.',
    includes: ['Campañas publicitarias (Meta/Google)', 'Automatizaciones y herramientas IA', 'Medición y optimización continua'],
  },
];

// ✅ “halo” por variante (para alternar verde/naranja)
const NEON_VARIANTS = {
  green: {
    ring: 'ring-1 ring-lime-400/35',
    halo: 'shadow-[0_0_55px_rgba(163,230,53,0.22),0_0_85px_rgba(34,197,94,0.16)]',
    bar: 'bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-300',
    barShadow: 'shadow-[0_0_16px_rgba(163,230,53,0.55),0_0_18px_rgba(34,197,94,0.35)]',
    bullet: 'bg-lime-300',
    bulletShadow: 'shadow-[0_0_14px_rgba(163,230,53,0.70)]',
    sysText: 'text-lime-300',
    hoverTitle: 'group-hover:text-lime-300',
    openIcon: 'text-lime-300',
  },
  orange: {
    ring: 'ring-1 ring-orange-400/35',
    halo: 'shadow-[0_0_55px_rgba(249,115,22,0.22),0_0_85px_rgba(251,146,60,0.16)]',
    bar: 'bg-gradient-to-r from-orange-400 via-amber-400 to-orange-300',
    barShadow: 'shadow-[0_0_16px_rgba(249,115,22,0.55),0_0_18px_rgba(251,146,60,0.35)]',
    bullet: 'bg-orange-300',
    bulletShadow: 'shadow-[0_0_14px_rgba(249,115,22,0.70)]',
    sysText: 'text-orange-300',
    hoverTitle: 'group-hover:text-orange-300',
    openIcon: 'text-orange-300',
  },
};

// ✅ decide variante por índice (alternando)
const getVariant = (index) => (index % 2 === 0 ? NEON_VARIANTS.green : NEON_VARIANTS.orange);

const ProcessCard = ({ item, open, onToggle, isVisible, delay = 0, variant }) => (
  <div
    className={[
      'group process-panel rounded-[2rem] p-10',
      'flex flex-col justify-end relative overflow-hidden',
      'transform transition-all duration-[1200ms] ease-out hover:z-20',
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10',
      // ✅ FIX: que SIEMPRE tenga ring/halo (antes solo cuando open, se veía "apagado" o invisible)
      `${variant.ring} ${variant.halo}`,
    ].join(' ')}
    style={{ transitionDelay: `${delay}ms` }}
  >
    <span className="frosted-number">{item.number}</span>

    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={[
        'absolute top-8 right-8 z-30',
        'w-11 h-11 rounded-2xl',
        'border border-white/40 dark:border-white/10',
        'bg-white/55 dark:bg-slate-950/30 backdrop-blur-xl',
        'flex items-center justify-center',
        'transition-all duration-300',
        open ? variant.openIcon : 'text-slate-700 dark:text-slate-200',
        'hover:scale-105 hover:bg-white/70 dark:hover:bg-white/10',
      ].join(' ')}
    >
      <span className="text-2xl leading-none">{open ? '−' : '+'}</span>
    </button>

    <div className="relative z-20">
      <div className={['w-16 h-1 mb-6', variant.bar, variant.barShadow].join(' ')} />

      <h3
        className={[
          'text-3xl md:text-[2.1rem] font-display font-black text-secondary dark:text-slate-100 uppercase tracking-wider mb-4 transition-colors',
          variant.hoverTitle,
        ].join(' ')}
      >
        {item.title}
      </h3>

      <p className="text-slate-500 dark:text-slate-300 font-light leading-relaxed text-base text-justify">
        {item.description}
      </p>

      <div
        className={[
          'grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
          open ? 'grid-rows-[1fr] opacity-100 mt-7' : 'grid-rows-[0fr] opacity-0 mt-0',
        ].join(' ')}
      >
        <div className="overflow-hidden">
          <div className="pt-6 border-t border-white/30 dark:border-white/10">
            <p className={['text-[10px] font-black tracking-[0.45em] uppercase mb-4', variant.sysText].join(' ')}>
              Incluye
            </p>

            <ul className="space-y-3">
              {item.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-3">
                  <span className={['mt-[6px] w-2 h-2 rounded-full', variant.bullet, variant.bulletShadow].join(' ')} />
                  <span className="text-slate-600 dark:text-slate-200 font-light leading-relaxed">{inc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Process() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    // ✅ IMPORTANTE: id="process" ya está correcto para el Header
    <section
      ref={sectionRef}
      className="pt-32 pb-20 relative overflow-hidden bg-[#EEF2F7] dark:bg-slate-950"
      id="process"
    >
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] dark:opacity-[0.02] z-0 mix-blend-multiply dark:mix-blend-normal" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-primary/5 dark:bg-primary/10 blur-[140px] rounded-full z-0" />

      <img
        src={logoPrincipal}
        alt=""
        className="absolute inset-0 w-full h-full object-contain opacity-[0.06] dark:opacity-[0.10] z-0"
        style={{
          transform: 'scale(1.25)',
          filter: 'grayscale(1) contrast(1.05)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* ✅ HEADER que faltaba: por eso “no se muestra process” (parecía vacío) */}
        <div
          className={[
            'mb-16 text-center transform transition-all duration-[1200ms] ease-out',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
          ].join(' ')}
        >
          <h2 className="text-5xl md:text-7xl font-display font-bold mt-6 text-secondary dark:text-slate-100 tracking-tight">
            PROCESO
          </h2>
          <p className="text-slate-500 dark:text-slate-300 mt-4 font-light text-lg max-w-4xl mx-auto">
            Así construimos una marca sólida: estrategia, sistema digital, contenido premium y activación basada en datos.
          </p>
        </div>

        {/* ✅ GRID de cards (ANTES estaba vacío, por eso no se veía nada) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {ACCORDION_ITEMS.map((item, idx) => {
            const open = openIndex === idx;
            const variant = getVariant(idx);
            return (
              <ProcessCard
                key={item.number}
                item={item}
                open={open}
                onToggle={() => setOpenIndex((prev) => (prev === idx ? -1 : idx))}
                isVisible={isVisible}
                delay={idx * 180}
                variant={variant}
              />
            );
          })}
        </div>

        {/* ✅ CTA */}
        <div
          className={[
            'mt-20',
            'group process-panel rounded-[2rem] p-10',
            'flex flex-col justify-end relative overflow-hidden',
            'transform transition-all duration-[1200ms] ease-out hover:z-20',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10',
          ].join(' ')}
          style={{ transitionDelay: `650ms` }}
        >
          <span className="frosted-number">05</span>

          <div className="relative z-20 text-center">
            <div
              className={[
                'w-16 h-1 mb-6 mx-auto',
                'bg-gradient-to-r from-lime-400 via-fuchsia-500 to-orange-400',
                'shadow-[0_0_16px_rgba(163,230,53,0.45),0_0_18px_rgba(217,70,239,0.30),0_0_18px_rgba(249,115,22,0.25)]',
              ].join(' ')}
            />

            <h3 className="text-3xl md:text-[2.1rem] font-display font-black text-secondary dark:text-slate-100 uppercase tracking-wider mb-4 group-hover:text-lime-300 transition-colors">
              Listo para llevar tu negocio al siguiente nivel?
            </h3>

            <p className="text-slate-500 dark:text-slate-300 font-light leading-relaxed text-base max-w-3xl mx-auto">
              Déjanos tus datos y te contactamos lo antes posible. Juntos veremos cómo hacer crecer tu marca y aumentar
              tus resultados en internet. ¡Anímate, te va a encantar!
            </p>

            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={scrollToContact}
                className="inline-flex items-center justify-center gap-3 rounded-2xl px-7 py-4 text-xs font-black tracking-[0.22em] uppercase bg-primary text-white hover:bg-[#2D54C7] transition-colors shadow-[0_0_32px_rgba(54,98,226,0.35)]"
              >
                Cotiza con nosotros
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}